import { useState } from 'react'
import type { FormEvent } from 'react'

import { api } from '../api/client'

import { useAuth } from '../auth/AuthContext'
import {
  Card,
  StatCard,
  ErrorBanner,
  formatDateTime,
  Spinner,
} from '../components/common'
import FlowFilterCard from '../components/FlowFilterCard'
import { ChiaveSegreti } from '../components/ChiaveSegreti'
import { DatiDiEsempio } from '../components/DatiDiEsempio'
import { AssistenteCard } from '../components/AssistenteCard'
import { SessioneCard } from '../components/SessioneCard'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'


const MIN_PASSWORD = 10

export default function Settings() {
  const { principal } = useAuth()
  const isAdmin =
    principal?.roles.includes('tenant_admin') || principal?.roles.includes('provider_admin')

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Impostazioni')}</h1>
          <p className="page__subtitle">{t('Gestisce licenza, sicurezza dell’accesso e configurazione del tenant')}</p>
        </div>
      </header>

      <LicenseCard />

      {/* Solo per chi amministra il tenant: decide che cosa si conserva del
          traffico di tutta l'organizzazione, e chi non ha quel ruolo non può
          comunque salvarlo — mostrarlo sarebbe un comando che fallisce. */}
      {isAdmin && <FlowFilterCard />}

      <OwnPassword />

      {/* Gli utenti hanno una sezione propria: erano in fondo a questa pagina,
          fra la licenza e la password propria, e chi cercava «dove aggiungo un
          collega» doveva scorrere impostazioni che non c'entrano. */}
      <Mfa />

      <AbuseIpdb />

      {/* La posta del tenant: chi non amministra non può salvarla, e un
          modulo che fallisce al salvataggio è peggio di un modulo assente. */}
      {isAdmin && <MailerCard />}

      {/* La chiave dei segreti del tenant: chi lo amministra la vede e la
          ruota da sé, senza chiederlo al fornitore. */}
      {isAdmin && <ChiaveSegreti />}

      {/* L'ambiente di esempio del tenant in prova, e il pulsante per
          passare all'ambiente vuoto quando arrivano i dati veri. */}
      {isAdmin && <DatiDiEsempio />}

      {/* L'assistente: con quale modello risponde, quota e mascheramento. */}
      {isAdmin && <AssistenteCard />}

      {/* La durata della sessione del tenant: politica di sicurezza, la decide
          chi amministra. */}
      {isAdmin && <SessioneCard />}


    </>
  )
}

/**
 * La chiave AbuseIPDB del tenant, per la reputazione degli IP pubblici.
 *
 * Del tenant e non della piattaforma: le sottoscrizioni ai servizi esterni
 * sono del cliente — la sua quota, il suo contratto — e una chiave condivisa
 * farebbe consumare a tutti la quota di uno.
 */
/**
 * La verifica in due passaggi della PROPRIA utenza.
 *
 * Il segreto compare una volta, qui, e mai più: si inquadra (o si copia
 * nell'app) e si conferma con un codice — senza la prova che l'app ce l'ha,
 * niente si attiva, o il primo logout chiuderebbe fuori la persona.
 */
function Mfa() {
  const stato = useAsync(() => api.mfaStatus(), [])
  const [setup, setSetup] = useState<{ secret: string; otpauth_uri: string } | null>(null)
  const [codice, setCodice] = useState('')
  const [busy, setBusy] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)

  async function avvia() {
    setBusy(true)
    setError(null)
    setEsito(null)
    try {
      setSetup(await api.mfaSetup())
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function attiva() {
    setBusy(true)
    setError(null)
    try {
      await api.mfaActivate(codice.trim())
      setSetup(null)
      setCodice('')
      setEsito(t('Verifica in due passaggi attiva: dal prossimo accesso servirà il codice.'))
      await stato.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function disattiva() {
    setBusy(true)
    setError(null)
    try {
      await api.mfaDisable(codice.trim())
      setCodice('')
      setEsito(t('Verifica in due passaggi disattivata.'))
      await stato.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Verifica in due passaggi')}>
      <ErrorBanner error={error ?? stato.error} />
      <p className="text-muted" style={{ marginTop: 0 }}>
        {t('Protegge l’accesso con un ulteriore fattore di autenticazione (Google Authenticator, Aegis, 1Password…)')}
      </p>

      {stato.data?.enabled && !setup && (
        <div className="rowactions" style={{ textAlign: 'left' }}>
          <span className="badge badge--ok">{t('attiva')}</span>
          <input
            value={codice}
            placeholder={t('codice per disattivare')}
            onChange={(e) => setCodice(e.target.value)}
            inputMode="numeric"
            maxLength={6}
            style={{ maxWidth: 160 }}
          />
          <button className="secondary" onClick={disattiva} disabled={busy || codice.trim().length < 6}>
            {t('Disattiva')}
          </button>
        </div>
      )}

      {!stato.data?.enabled && !setup && (
        <button onClick={avvia} disabled={busy}>
          {t('Attiva la verifica')}
        </button>
      )}

      {setup && (
        <>
          <p style={{ marginBottom: 4 }}>
            {t('1. Aggiungi la chiave all’app di autenticazione (inserimento manuale, o apri il collegamento dal telefono):')}
          </p>
          <p className="mono" style={{ fontSize: 18, letterSpacing: '0.1em', wordBreak: 'break-all' }}>
            {setup.secret.replace(/(.{4})/g, '$1 ').trim()}
          </p>
          <p>
            <a href={setup.otpauth_uri}>{t('apri nell’app di autenticazione')}</a>
          </p>
          <p style={{ marginBottom: 4 }}>{t('2. Conferma con il codice che l’app mostra adesso:')}</p>
          <div className="rowactions" style={{ textAlign: 'left' }}>
            <input
              value={codice}
              onChange={(e) => setCodice(e.target.value)}
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              style={{ maxWidth: 140, letterSpacing: '0.2em' }}
            />
            <button onClick={attiva} disabled={busy || codice.trim().length < 6}>
              {t('Conferma e attiva')}
            </button>
          </div>
        </>
      )}
      {esito && <p className="field__hint">{esito}</p>}
    </Card>
  )
}


function AbuseIpdb() {
  const stato = useAsync(() => api.abuseipdb(), [])
  const [chiave, setChiave] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function salva(valore: string) {
    setBusy(true)
    setError(null)
    try {
      await api.setAbuseipdb(valore)
      setChiave('')
      await stato.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Reputazione IP (AbuseIPDB)')}>
      <ErrorBanner error={error ?? stato.error} />
      <p className="text-muted" style={{ marginTop: 0 }}>
        {t('Con una chiave API, la verifica della reputazione degli IP pubblici utilizza anche AbuseIPDB, oltre alle tre DNSBL. La chiave e la relativa quota sono associate al contratto. Il piano gratuito prevede 1000 verifiche giornaliere.')}
      </p>
      <div className="rowactions" style={{ textAlign: 'left' }}>
        <input
          type="password"
          value={chiave}
          placeholder={
            stato.data?.configured
              ? t('configurata — incolla una chiave per sostituirla')
              : t('chiave API di AbuseIPDB')
          }
          onChange={(e) => setChiave(e.target.value)}
          style={{ maxWidth: 340 }}
        />
        <button onClick={() => salva(chiave)} disabled={busy || !chiave.trim()}>
          {t('Salva')}
        </button>
        {stato.data?.configured && (
          <button className="secondary" onClick={() => salva('')} disabled={busy}>
            {t('Togli la chiave')}
          </button>
        )}
      </div>
    </Card>
  )
}


function LicenseCard() {
  const license = useAsync(() => api.license(), [])

  if (license.loading && !license.data) return <Spinner />
  if (!license.data) return <ErrorBanner error={license.error} />

  const { plan, status, features, quotas, usage, remaining, valid_until } = license.data
  const missing = license.data.missing_from_plan ?? []
  // I moduli col loro nome leggibile, in ordine alfabetico.
  const nomi = license.data.feature_labels ?? {}
  const nome = (f: string) => nomi[f] ?? f
  const perNome = (a: string, b: string) => nome(a).localeCompare(nome(b))
  const enabled = Object.entries(features).filter(([, on]) => on).map(([name]) => name).sort(perNome)
  const disabled = Object.entries(features).filter(([, on]) => !on).map(([name]) => name).sort(perNome)

  return (
    <Card title={t('Licenza')}>
      <div className="stats" style={{ marginBottom: 16 }}>
        <StatCard label={t('Piano')} value={plan} hint={status} />
        <StatCard
          label={t('Configuration Item')}
          value={quotas.max_cis ? `${usage.cis}/${quotas.max_cis}` : String(usage.cis)}
          tone={remaining.cis === 0 ? 'error' : undefined}
        />
        <StatCard
          label={t('Sonde')}
          value={
            quotas.max_appliances
              ? `${usage.appliances}/${quotas.max_appliances}`
              : String(usage.appliances)
          }
          tone={remaining.appliances === 0 ? 'error' : undefined}
        />
      </div>

      {valid_until && (
        <p className="field__hint">{t('Valida fino al {data}.', { data: formatDateTime(valid_until) })}</p>
      )}

      {missing.length > 0 && (
        <div className="banner banner--info" style={{ marginTop: 12 }}>
          {t('Il piano {piano} comprende {funzioni}, ma la licenza è stata emessa prima che questa funzione esistesse e non la concede. Chiedi al fornitore di riassegnare lo stesso piano per attivarla.', { piano: plan, funzioni: missing.map(nome).join(', ') })}
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <strong style={{ fontSize: 12 }}>{t('Comprese:')} </strong>
        {enabled.length ? (
          enabled.map((f) => (
            <span className="chip" key={f}>
              {nome(f)}
            </span>
          ))
        ) : (
          <span className="field__hint">{t('nessuna')}</span>
        )}
      </div>
      {disabled.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <strong style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('Non comprese:')} </strong>
          {disabled.map((f) => (
            <span className="chip" key={f} style={{ opacity: 0.6 }}>
              {nome(f)}
            </span>
          ))}
        </div>
      )}

      {(remaining.cis === 0 || remaining.appliances === 0) && (
        <div className="banner banner--error" style={{ marginTop: 12, marginBottom: 0 }}>
          {t('Quota esaurita: i nuovi CI non verranno registrati finché il piano non viene ampliato. Quelli già noti continuano ad aggiornarsi.')}
        </div>
      )}
    </Card>
  )
}

/**
 * Assegnazione delle licenze ai tenant: visibile solo a chi amministra la
 * piattaforma. Un tenant non può modificare il proprio piano, altrimenti la
 * licenza non governerebbe più nulla.
 */
function OwnPassword() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setDone(false)

    if (next !== confirm) {
      setError(new Error(t('La nuova password e la conferma non coincidono')))
      return
    }
    setBusy(true)
    try {
      await api.changeOwnPassword(current, next)
      setCurrent('')
      setNext('')
      setConfirm('')
      setDone(true)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('La mia password')}>
      <ErrorBanner error={error} />
      {done && <div className="banner banner--ok">{t('Password aggiornata.')}</div>}
      <form onSubmit={onSubmit}>
        <div className="row">
          <div className="field">
            <label htmlFor="cur">{t('Password attuale')}</label>
            <input
              id="cur"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="new">{t('Nuova password')}</label>
            <input
              id="new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              minLength={MIN_PASSWORD}
              autoComplete="new-password"
              required
            />
            <p className="field__hint">{t('Almeno {n} caratteri.', { n: MIN_PASSWORD })}</p>
          </div>
          <div className="field">
            <label htmlFor="conf">{t('Conferma')}</label>
            <input
              id="conf"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={MIN_PASSWORD}
              autoComplete="new-password"
              required
            />
          </div>
        </div>
        <button type="submit" disabled={busy}>
          {busy ? t('Aggiornamento…') : t('Cambia password')}
        </button>
      </form>
    </Card>
  )
}




/**
 * La posta in uscita del tenant.
 *
 * Il default è il relay della piattaforma; il canale proprio serve a chi vuole
 * che gli avvisi escano dal SUO dominio, con le sue policy (SPF e DKIM sono
 * suoi). Insieme al canale, le notifiche: i destinatari e cosa mandare — un
 * canale senza destinatari è un mailer che non manda niente, e va detto qui.
 */
function MailerCard() {
  const stato = useAsync(() => api.tenantMailer(), [])
  const [mode, setMode] = useState<'platform' | 'custom' | null>(null)
  const [host, setHost] = useState<string | null>(null)
  const [port, setPort] = useState<number | null>(null)
  const [security, setSecurity] = useState<string | null>(null)
  const [username, setUsername] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [fromAddress, setFromAddress] = useState<string | null>(null)
  const [fromName, setFromName] = useState<string | null>(null)
  const [recipients, setRecipients] = useState<string | null>(null)
  const [notifyDob, setNotifyDob] = useState<boolean | null>(null)
  const [notifyHp, setNotifyHp] = useState<boolean | null>(null)
  const [notifyCtr, setNotifyCtr] = useState<boolean | null>(null)
  const [notifyNis2, setNotifyNis2] = useState<boolean | null>(null)
  const [notifyBl, setNotifyBl] = useState<boolean | null>(null)
  const [notifyCra, setNotifyCra] = useState<boolean | null>(null)
  const [notifySuppliers, setNotifySuppliers] = useState<boolean | null>(null)
  const [notifyMedical, setNotifyMedical] = useState<boolean | null>(null)
  const [provaA, setProvaA] = useState('')
  const [busy, setBusy] = useState(false)
  const [esito, setEsito] = useState('')
  const [errore, setErrore] = useState<unknown>(null)

  const dati = stato.data
  if (stato.loading && !dati) return <Card title={t('Posta in uscita e notifiche')}><Spinner /></Card>
  if (!dati) return <Card title={t('Posta in uscita e notifiche')}><ErrorBanner error={stato.error} /></Card>

  // Lo stato locale parte dal server e si stacca solo quando l'utente tocca.
  const vMode = mode ?? dati.mode
  const vHost = host ?? dati.host
  const vPort = port ?? dati.port
  const vSecurity = security ?? dati.security
  const vUsername = username ?? dati.username
  const vFrom = fromAddress ?? dati.from_address
  const vFromName = fromName ?? dati.from_name
  const vRecipients = recipients ?? dati.recipients.join(', ')
  const vNotifyDob = notifyDob ?? dati.notify_dobermann
  const vNotifyHp = notifyHp ?? dati.notify_honeypot
  const vNotifyCtr = notifyCtr ?? dati.notify_contracts
  const vNotifyNis2 = notifyNis2 ?? dati.notify_nis2 ?? true
  const vNotifyBl = notifyBl ?? dati.notify_bitlocker ?? true
  const vNotifyCra = notifyCra ?? dati.notify_cra ?? true
  const vNotifySuppliers = notifySuppliers ?? dati.notify_suppliers ?? true
  const vNotifyMedical = notifyMedical ?? dati.notify_medical ?? true

  async function salva() {
    setBusy(true)
    setErrore(null)
    setEsito('')
    try {
      await api.setTenantMailer({
        mode: vMode,
        host: vHost,
        port: vPort,
        security: vSecurity as 'starttls' | 'ssl' | 'none',
        username: vUsername,
        ...(password ? { password } : {}),
        from_address: vFrom,
        from_name: vFromName,
        recipients: vRecipients.split(',').map((r) => r.trim()).filter(Boolean),
        notify_dobermann: vNotifyDob,
        notify_honeypot: vNotifyHp,
        notify_contracts: vNotifyCtr,
        notify_nis2: vNotifyNis2,
        notify_bitlocker: vNotifyBl,
        notify_cra: vNotifyCra,
        notify_suppliers: vNotifySuppliers,
        notify_medical: vNotifyMedical,
      })
      setPassword('')
      setEsito(t('Salvata.'))
      await stato.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  async function prova() {
    if (!provaA.trim()) return
    setBusy(true)
    setErrore(null)
    setEsito('')
    try {
      await api.testTenantMailer(provaA.trim())
      setEsito(t('Messaggio di prova consegnato al relay.'))
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Posta in uscita e notifiche')}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Definisce il canale di invio degli avvisi Dobermann e i relativi destinatari. Il canale della piattaforma è adatto alla maggior parte degli scenari; un canale personalizzato consente di inviare le e-mail dal proprio dominio, applicando le relative policy.')}
      </p>

      <div className="field">
        <label>
          <input type="radio" name="mailer-mode" checked={vMode === 'platform'}
                 onChange={() => setMode('platform')} />{' '}
          {t('Relay della piattaforma')}
        </label>
        <label style={{ marginLeft: 16 }}>
          <input type="radio" name="mailer-mode" checked={vMode === 'custom'}
                 onChange={() => setMode('custom')} />{' '}
          {t('SMTP del tenant')}
        </label>
      </div>

      {vMode === 'custom' && (
        <div className="mailer-campi">
          <label>
            {t('Host SMTP')}
            <input value={vHost} onChange={(e) => setHost(e.target.value)}
                   placeholder="smtp.azienda.it" />
          </label>
          <label>
            {t('Porta')}
            <input type="number" value={vPort}
                   onChange={(e) => setPort(Number(e.target.value) || 0)} />
          </label>
          <label>
            {t('Sicurezza')}
            <select value={vSecurity} onChange={(e) => setSecurity(e.target.value)}>
              <option value="starttls">STARTTLS</option>
              <option value="ssl">SSL/TLS</option>
              <option value="none">{t('nessuna (relay interno)')}</option>
            </select>
          </label>
          <label>
            {t('Utente')}
            <input value={vUsername} onChange={(e) => setUsername(e.target.value)} />
          </label>
          <label>
            {t('Password')}
            <input type="password" value={password}
                   onChange={(e) => setPassword(e.target.value)}
                   placeholder={dati.has_password ? t('(salvata: lascia vuoto per non cambiarla)') : ''} />
          </label>
          <label>
            {t('Mittente')}
            <input value={vFrom} onChange={(e) => setFromAddress(e.target.value)}
                   placeholder="censo@azienda.it" />
          </label>
          <label>
            {t('Nome mittente')}
            <input value={vFromName} onChange={(e) => setFromName(e.target.value)}
                   placeholder="CENSO" />
          </label>
        </div>
      )}

      <label style={{ display: 'block', marginTop: 12 }}>
        {t('Destinatari degli avvisi (separati da virgola)')}
        <input value={vRecipients} onChange={(e) => setRecipients(e.target.value)}
               placeholder={t('noc@azienda.it, sicurezza@azienda.it')}
               style={{ display: 'block', width: '100%', marginTop: 4 }} />
      </label>

      <div className="checkbox" style={{ marginTop: 10 }}>
        <input id="notif-dob" type="checkbox" checked={vNotifyDob}
               onChange={(e) => setNotifyDob(e.target.checked)} />
        <label htmlFor="notif-dob">
          {t('Notifica nuove rilevazioni da Dobermann.')}
          <small>{t('Viene inviata una sola e-mail per ogni ciclo di controllo, solamente quando viene rilevato un indirizzo mai censito.')}</small>
        </label>
      </div>
      <div className="checkbox">
        <input id="notif-hp" type="checkbox" checked={vNotifyHp}
               onChange={(e) => setNotifyHp(e.target.checked)} />
        <label htmlFor="notif-hp">
          {t('Notifica ogni connessione a un honeypot.')}
          <small>{t('Non è previsto traffico legittimo verso questi sistemi. Ogni connessione viene considerata un allarme.')}</small>
        </label>
      </div>
      <div className="checkbox">
        <input id="notif-ctr" type="checkbox" checked={vNotifyCtr}
               onChange={(e) => setNotifyCtr(e.target.checked)} />
        <label htmlFor="notif-ctr">
          {t('Avvisa sulle scadenze dei contratti')}
          <small>{t('Notifica le scadenze dei contratti alle soglie previste, ad esempio 90, 30 e 7 giorni prima della data di riferimento o secondo le soglie configurate nel contratto. Gli avvisi possono essere inviati anche al responsabile del contratto.')}</small>
        </label>
      </div>
      <div className="checkbox">
        <input id="notif-nis2" type="checkbox" checked={vNotifyNis2}
               onChange={(e) => setNotifyNis2(e.target.checked)} />
        <label htmlFor="notif-nis2">
          {t('Avvisa sulle scadenze NIS2 e sugli incidenti')}
          <small>{t('Adempimenti NIS2 a 30, 7 e 1 giorno dalla scadenza e quando scadono; per gli incidenti, prima della scadenza di pre-notifica, notifica e relazione finale. Gli avvisi vanno anche al responsabile e al punto di contatto NIS2.')}</small>
        </label>
      </div>
      <div className="checkbox">
        <input id="notif-bl" type="checkbox" checked={vNotifyBl}
               onChange={(e) => setNotifyBl(e.target.checked)} />
        <label htmlFor="notif-bl">
          {t('Avvisa quando viene letta una chiave BitLocker')}
          <small>{t('Ogni lettura di una chiave di ripristino BitLocker: macchina, volume, chi l’ha letta, quando e perché. La chiave non è mai nella mail.')}</small>
        </label>
      </div>
      <div className="checkbox">
        <input id="notif-cra" type="checkbox" checked={vNotifyCra}
               onChange={(e) => setNotifyCra(e.target.checked)} />
        <label htmlFor="notif-cra">
          {t('Avvisa sul registro CRA')}
          <small>{t('Le vulnerabilità sfruttate attivamente trovate nei rilasci supportati dei prodotti in ambito, da valutare per la segnalazione; i termini delle segnalazioni (preallerta, notifica, relazione intermedia, rapporto finale) prima che scadano e quando scadono. Gli avvisi vanno anche al responsabile del prodotto.')}</small>
        </label>
      </div>
      <div className="checkbox">
        <input id="notif-fornitori" type="checkbox" checked={vNotifySuppliers}
               onChange={(e) => setNotifySuppliers(e.target.checked)} />
        <label htmlFor="notif-fornitori">
          {t('Avvisa sulle richieste ai fornitori')}
          <small>{t('Una richiesta a un fornitore (vulnerabilità, fine supporto, SBOM) rimasta senza risposta alla scadenza. Il sollecito al fornitore parte comunque, in copia al referente.')}</small>
        </label>
      </div>
      <div className="checkbox">
        <input id="notif-medicali" type="checkbox" checked={vNotifyMedical}
               onChange={(e) => setNotifyMedical(e.target.checked)} />
        <label htmlFor="notif-medicali">
          {t('Avvisa su manutenzioni, verifiche e richiami degli elettromedicali')}
          <small>{t('CENSO MED: le manutenzioni e le verifiche di sicurezza elettrica in scadenza (60, 30, 7 giorni prima e alla scadenza), ogni settimana quelle scadute, e i nuovi richiami delle autorità sui modelli dei vostri elettromedicali. Gli avvisi vanno anche ai destinatari scelti nell’area CENSO MED.')}</small>
        </label>
      </div>

      <div className="toolbar" style={{ marginTop: 12 }}>
        <button type="button" onClick={salva} disabled={busy}>
          {t('Salva')}
        </button>
        <input value={provaA} onChange={(e) => setProvaA(e.target.value)}
               placeholder={t('indirizzo per la prova')} aria-label={t('indirizzo per la prova')} />
        <button type="button" className="secondary" onClick={prova}
                disabled={busy || !provaA.trim()}>
          {t('Invia prova')}
        </button>
        {esito && <span className="muted">{esito}</span>}
      </div>
      <ErrorBanner error={errore} />
    </Card>
  )
}
